/**
 * theme.js — shared UI behaviour for thebetterpuff.
 *
 * Owns chrome only: drawers, navigation, accordions, galleries, variant
 * selection and quantity inputs. It never stores cart state — see cart.js,
 * which talks to the Shopify Cart Ajax API.
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------------------------------------------------------------- *
   * Drawers
   * ---------------------------------------------------------------- */

  var Drawers = {
    active: null,
    lastFocused: null,

    get: function (name) {
      return document.querySelector('[data-drawer="' + name + '"]');
    },

    open: function (name) {
      var drawer = this.get(name);
      if (!drawer) return;

      if (this.active && this.active !== drawer) this.close({ restoreFocus: false });

      this.lastFocused = document.activeElement;
      this.active = drawer;

      drawer.setAttribute('data-open', 'true');
      drawer.removeAttribute('aria-hidden');
      document.body.classList.add('is-locked');
      this.syncTriggers(name, true);

      var panel = drawer.querySelector('.drawer__panel');
      var target = drawer.querySelector('[data-drawer-autofocus]') || panel;
      if (target) {
        if (target === panel && !panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    },

    close: function (options) {
      var opts = options || {};
      var drawer = this.active;
      if (!drawer) return;

      drawer.setAttribute('data-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      this.syncTriggers(drawer.getAttribute('data-drawer'), false);
      this.active = null;

      if (opts.restoreFocus !== false && this.lastFocused && this.lastFocused.focus) {
        this.lastFocused.focus({ preventScroll: true });
      }
      this.lastFocused = null;
    },

    toggle: function (name) {
      var drawer = this.get(name);
      if (drawer && drawer.getAttribute('data-open') === 'true') {
        this.close();
      } else {
        this.open(name);
      }
    },

    syncTriggers: function (name, expanded) {
      document.querySelectorAll('[data-drawer-open="' + name + '"]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    },

    trapFocus: function (event) {
      if (!this.active) return;
      var panel = this.active.querySelector('.drawer__panel');
      if (!panel) return;

      var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (node) {
        return node.offsetParent !== null;
      });
      if (!nodes.length) return;

      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      Drawers.toggle(opener.getAttribute('data-drawer-open'));
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      Drawers.close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!Drawers.active) return;
    if (event.key === 'Escape') {
      Drawers.close();
    } else if (event.key === 'Tab') {
      Drawers.trapFocus(event);
    }
  });

  /* ---------------------------------------------------------------- *
   * Sticky header state
   * ---------------------------------------------------------------- */

  function initHeaderScroll() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var update = function () {
      header.setAttribute('data-scrolled', window.scrollY > 4 ? 'true' : 'false');
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ---------------------------------------------------------------- *
   * Accordions — one open item per group
   * ---------------------------------------------------------------- */

  function initAccordions(scope) {
    (scope || document).querySelectorAll('[data-accordion-single]').forEach(function (group) {
      group.querySelectorAll('details').forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          group.querySelectorAll('details[open]').forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------- *
   * Quantity inputs
   * ---------------------------------------------------------------- */

  function initQuantity(scope) {
    (scope || document).querySelectorAll('[data-quantity]').forEach(function (widget) {
      if (widget.dataset.quantityReady === 'true') return;
      widget.dataset.quantityReady = 'true';

      var input = widget.querySelector('[data-quantity-input]');
      if (!input) return;

      widget.querySelectorAll('[data-quantity-change]').forEach(function (button) {
        button.addEventListener('click', function () {
          var step = parseInt(button.getAttribute('data-quantity-change'), 10) || 0;
          var min = parseInt(input.getAttribute('min'), 10);
          var next = (parseInt(input.value, 10) || 0) + step;
          if (!isNaN(min) && next < min) next = min;
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------------------------------------------------------------- *
   * Product gallery
   * ---------------------------------------------------------------- */

  function initGalleries(scope) {
    (scope || document).querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';

      var mainImage = gallery.querySelector('.gallery__main img');
      if (!mainImage) return;

      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var thumbImage = thumb.querySelector('img');
          if (!thumbImage) return;

          if (thumbImage.dataset.fullSrc) mainImage.src = thumbImage.dataset.fullSrc;
          if (thumbImage.dataset.fullSrcset) mainImage.srcset = thumbImage.dataset.fullSrcset;
          mainImage.alt = thumbImage.alt || '';

          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (other) {
            other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------- *
   * Variant selection
   *
   * Variant data (including money-formatted prices) is rendered by Liquid,
   * so the browser never formats currency or guesses availability.
   * ---------------------------------------------------------------- */

  function initVariantPickers(scope) {
    (scope || document).querySelectorAll('[data-product-form]').forEach(function (form) {
      if (form.dataset.variantReady === 'true') return;
      form.dataset.variantReady = 'true';

      var dataScript = form.querySelector('[data-variant-data]');
      if (!dataScript) return;

      var variants;
      try {
        variants = JSON.parse(dataScript.textContent);
      } catch (error) {
        return;
      }
      if (!Array.isArray(variants) || !variants.length) return;

      var idInput = form.querySelector('[data-variant-id]');
      var priceTarget = form.querySelector('[data-variant-price]');
      var submit = form.querySelector('[data-add-to-cart]');
      var skuTarget = form.querySelector('[data-variant-sku]');
      var soldOutLabel = submit ? submit.getAttribute('data-sold-out-label') : null;
      var addLabel = submit ? submit.getAttribute('data-add-label') : null;

      var readSelections = function () {
        var values = [];
        form.querySelectorAll('[data-option-index]').forEach(function (control) {
          var index = parseInt(control.getAttribute('data-option-index'), 10);
          if (isNaN(index)) return;
          if (control.tagName === 'SELECT') {
            values[index] = control.value;
          } else if (control.checked) {
            values[index] = control.value;
          }
        });
        return values;
      };

      var findVariant = function (selections) {
        return variants.filter(function (variant) {
          return selections.every(function (value, index) {
            return value === undefined || variant.options[index] === value;
          });
        })[0];
      };

      var update = function () {
        var selections = readSelections();
        var variant = findVariant(selections);

        form.querySelectorAll('[data-selected-value-for]').forEach(function (output) {
          var index = parseInt(output.getAttribute('data-selected-value-for'), 10);
          output.textContent = selections[index] || '';
        });

        if (!variant) {
          if (submit) {
            submit.disabled = true;
            if (soldOutLabel) submit.textContent = soldOutLabel;
          }
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (priceTarget && variant.price_html) priceTarget.innerHTML = variant.price_html;
        if (skuTarget) skuTarget.textContent = variant.sku || '';

        if (submit) {
          submit.disabled = !variant.available;
          if (variant.available && addLabel) {
            submit.textContent = addLabel;
          } else if (!variant.available && soldOutLabel) {
            submit.textContent = soldOutLabel;
          }
        }

        if (variant.url && window.history && window.history.replaceState) {
          window.history.replaceState({}, '', variant.url);
        }
      };

      form.querySelectorAll('[data-option-index]').forEach(function (control) {
        control.addEventListener('change', update);
      });

      update();
    });
  }

  /* ---------------------------------------------------------------- *
   * Boot
   * ---------------------------------------------------------------- */

  function init(scope) {
    initAccordions(scope);
    initQuantity(scope);
    initGalleries(scope);
    initVariantPickers(scope);
  }

  window.theme = window.theme || {};
  window.theme.drawers = Drawers;
  window.theme.init = init;

  document.addEventListener('DOMContentLoaded', function () {
    initHeaderScroll();
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    initHeaderScroll();
  });
})();
